import { useCallback, useState } from 'react';

export type ComposerBackgroundMode =
  | 'default'
  | 'black'
  | 'white'
  | 'gray'
  | 'custom';

export const COMPOSER_BACKGROUND_PRESET_COLORS: Record<
  'black' | 'white' | 'gray',
  string
> = {
  black: '#000000',
  white: '#ffffff',
  gray: '#888888'
};

export interface ComposerBackgroundInitialState {
  mode: ComposerBackgroundMode;
  customColor?: string;
}

// A restored scene's background is only a real user choice if its color
// isn't one of the theme's own surface colors — otherwise it's just what the
// theme-sync effect last wrote, and 'default' (follow the theme) is correct.
export function resolveComposerBackgroundInitialState(
  background: { mode: 'color' | 'gradient'; color?: string } | undefined,
  themeBackgroundColor: string
): ComposerBackgroundInitialState {
  const color = background?.mode === 'color' ? background.color : undefined;

  if (!color || color === themeBackgroundColor) return { mode: 'default' };

  const presetEntry = (
    Object.entries(COMPOSER_BACKGROUND_PRESET_COLORS) as Array<
      [keyof typeof COMPOSER_BACKGROUND_PRESET_COLORS, string]
    >
  ).find(([, presetColor]) => presetColor === color);

  if (presetEntry) return { mode: presetEntry[0] };

  return { mode: 'custom', customColor: color };
}

function useComposerBackground(
  defaultColor: string,
  initialState?: ComposerBackgroundInitialState
) {
  const [backgroundMode, setBackgroundMode] = useState<ComposerBackgroundMode>(
    initialState?.mode ?? 'default'
  );
  const [customColor, setCustomColorState] = useState(
    initialState?.customColor ?? '#ffffff'
  );

  const setPresetBackground = useCallback(
    (mode: 'default' | 'black' | 'white' | 'gray') => {
      setBackgroundMode(mode);
    },
    []
  );

  const setCustomColor = useCallback((hex: string) => {
    setBackgroundMode('custom');
    setCustomColorState(hex);
  }, []);

  let backgroundColor = defaultColor;
  if (backgroundMode === 'black')
    backgroundColor = COMPOSER_BACKGROUND_PRESET_COLORS.black;
  else if (backgroundMode === 'white')
    backgroundColor = COMPOSER_BACKGROUND_PRESET_COLORS.white;
  else if (backgroundMode === 'gray')
    backgroundColor = COMPOSER_BACKGROUND_PRESET_COLORS.gray;
  else if (backgroundMode === 'custom') backgroundColor = customColor;

  return {
    backgroundMode,
    customColor,
    backgroundColor,
    setPresetBackground,
    setCustomColor
  };
}

export default useComposerBackground;
